import React, { useState, useEffect } from 'react';
import { X, Sparkles, Loader2, Upload, Plus } from 'lucide-react';
import { toast } from 'sonner';

const enconOrange = '#ee7203';

/**
 * "Key Information" modal — captured BEFORE the script is generated.
 * Mode = "blank" (no upload) or "upload" (drag/drop prior meeting notes).
 *
 * Representative / witness / active-warning are NOT captured here. Those
 * are always rendered as standard tickable items in the script so the
 * chair can tick them on the printed document during the hearing.
 */
const DRAFT_KEY = 'disciplinary_keyinfo_draft_v1';

const blank = () => ({
  employee_name: '',
  manager_name: '',
  notetaker: '',
  date: new Date().toISOString().slice(0, 10),
  start_time: '09:00',
  location: '',
  allegations: '',
  notice_48h: true,
  letter_sent: true,
});

const DisciplinaryKeyInfoModal = ({ isOpen, mode, onClose, onSubmit, busy }) => {
  const [form, setForm] = useState(blank());
  const [file, setFile] = useState(null);

  // Lock body scroll while modal is open, and restore on close
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  // Load draft on first open of a session
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) setForm(prev => ({ ...prev, ...JSON.parse(saved) }));
    } catch (_) { /* noop */ }
  }, [isOpen]);

  // Auto-save draft on every edit (no password needed — draft only)
  useEffect(() => {
    if (!isOpen) return;
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch (_) { /* noop */ }
  }, [form, isOpen]);

  if (!isOpen) return null;

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleClose = () => { if (!busy) onClose(); };

  const handleClearDraft = () => {
    if (!window.confirm('Discard the current draft and start fresh?')) return;
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (_) { /* noop */ }
    setForm(blank());
    setFile(null);
    toast.success('Draft cleared');
  };

  const handleGo = () => {
    if (!form.employee_name.trim()) { toast.error('Employee name is required'); return; }
    if (!form.manager_name.trim()) { toast.error('Disciplinary manager name is required'); return; }
    if (!form.allegations.trim()) { toast.error('Please type the full allegation'); return; }
    if (mode === 'upload' && !file) { toast.error('Please attach the prior meeting notes'); return; }

    onSubmit({ keyInfo: { ...form }, file });
  };

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    const ok = ['pdf', 'docx', 'doc', 'png', 'jpg', 'jpeg', 'webp', 'heic'].includes(ext);
    if (!ok) { toast.error('Use PDF, DOCX, or an image (PNG/JPG/WEBP/HEIC)'); return; }
    setFile(f);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="disc-keyinfo-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        style={{ border: `3px solid ${enconOrange}` }}
      >
        {/* Header (fixed) */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 shrink-0">
          <Sparkles className="w-5 h-5" style={{ color: enconOrange }} />
          <h3 className="text-lg font-extrabold text-gray-900">
            {mode === 'upload' ? 'New Disciplinary — From Prior Meeting Notes' : 'New Disciplinary Hearing Script'}
          </h3>
          <button
            onClick={handleClose}
            disabled={busy}
            className="ml-auto text-gray-400 hover:text-gray-700 disabled:opacity-50"
            data-testid="disc-keyinfo-close"
            title="Close (your draft is auto-saved)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scrolls internally) */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Employee Name *">
              <input data-testid="disc-employee-name" value={form.employee_name} onChange={(e) => setField('employee_name', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Disciplinary Manager *">
              <input data-testid="disc-manager-name" value={form.manager_name} onChange={(e) => setField('manager_name', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Notetaker">
              <input data-testid="disc-notetaker" value={form.notetaker} onChange={(e) => setField('notetaker', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Date">
              <input type="date" data-testid="disc-date" value={form.date} onChange={(e) => setField('date', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Start Time">
              <input type="time" data-testid="disc-time" value={form.start_time} onChange={(e) => setField('start_time', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Location">
              <input data-testid="disc-location" value={form.location} onChange={(e) => setField('location', e.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="Full Allegation(s) *  — typed verbatim, will appear in the script word-for-word">
            <textarea
              data-testid="disc-allegations"
              value={form.allegations}
              onChange={(e) => setField('allegations', e.target.value)}
              rows={4}
              className={`${inputCls} font-mono text-sm`}
              placeholder="On 12 March 2026, you left the warehouse during your shift without notifying your supervisor and were observed by..."
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="48 hours' notice given?">
              <Toggle on={form.notice_48h} onChange={(v) => setField('notice_48h', v)} testid="disc-notice-toggle" />
            </Field>
            <Field label="Invite letter & evidence pack sent in advance?">
              <Toggle on={form.letter_sent} onChange={(v) => setField('letter_sent', v)} testid="disc-letter-toggle" />
            </Field>
          </div>

          <p className="text-[11px] text-gray-500 italic">
            Representative, witness and active-warning items are rendered as tickable checkboxes in the script so you can mark them off live during the hearing.
          </p>

          {mode === 'upload' && (
            <Field label="Prior meeting notes (PDF / DOCX / image of handwriting) *">
              <UploadDropzone file={file} onFile={handleFile} disabled={busy} />
            </Field>
          )}
        </div>

        {/* Footer (fixed) */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 shrink-0">
          <button
            onClick={handleClearDraft}
            disabled={busy}
            className="text-xs text-gray-400 hover:text-red-600 underline disabled:opacity-50"
            data-testid="disc-keyinfo-clear-draft"
          >
            Clear draft
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={busy}
              className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              data-testid="disc-keyinfo-cancel"
            >
              Close
            </button>
            <button
              onClick={handleGo}
              disabled={busy}
              className="px-6 py-2 rounded-lg text-white font-semibold shadow-md disabled:opacity-60 inline-flex items-center gap-2"
              style={{ backgroundColor: enconOrange }}
              data-testid="disc-keyinfo-submit"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                : <><Plus className="w-4 h-4" /> Generate Script</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">{label}</span>
    {children}
  </label>
);

const Toggle = ({ on, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    data-testid={testid}
    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${on ? 'bg-orange-500' : 'bg-gray-300'}`}
    aria-pressed={on}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const UploadDropzone = ({ file, onFile, disabled }) => {
  const [drag, setDrag] = useState(false);
  const inputRef = React.useRef(null);
  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer?.files?.[0]; if (f) onFile(f);
      }}
      className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
        drag ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50'
      }`}
      data-testid="disc-upload-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,.heic"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }}
        data-testid="disc-upload-input"
      />
      <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: enconOrange }} />
      {file ? (
        <div>
          <div className="text-sm font-semibold text-gray-900">{file.name}</div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="mt-2 text-xs underline text-orange-600 hover:text-orange-700"
          >
            Change file
          </button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-700">Drop the file here</p>
          <p className="text-xs text-gray-500 mt-1">or</p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: enconOrange }}
          >
            <Upload className="w-3.5 h-3.5" /> Browse
          </button>
          <p className="text-[11px] text-gray-400 mt-2">PDF · DOCX · PNG · JPG · WEBP · HEIC (handwriting OK)</p>
        </div>
      )}
    </div>
  );
};

export default DisciplinaryKeyInfoModal;
